import {
  ArrowRight,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Sparkles,
} from "lucide-react";
import moment from "moment";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useLocalStorage from "react-use-localstorage";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { OPEN_VIBECHAT_EVENT, OpenVibeChatEventDetail } from "@/components/VibeChat/constants";
import { useUser } from "@/context/user-context";
import { cn } from "@/lib/utils";

const FEATURE_DATE_FORMAT = "MM-DD-YYYY";
const STORAGE_KEY_VERSION = "lastSeenFeatureVersion";
const STORAGE_KEY_DISMISSED = "featureModalDismissed";
const STORAGE_KEY_PREVIEW_MODE = "preview_mode";
export const OPEN_NEW_FEATURE_MODAL_EVENT = "promptlayer:open-new-feature-modal";
export const NEW_FEATURE_MODAL_SEEN_EVENT = "promptlayer:new-feature-modal-seen";

interface FeatureSection {
  heading: string;
  body: string;
  image?: string;
  imageAlt?: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
}

interface FeatureModalConfig {
  id: string;
  releasedAt: string;
  title: string;
  subtitle: string;
  image?: string;
  video?: string;
  /** Short teaser shown in the media placeholder when there is no image or video. */
  mediaHook?: string;
  link?: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
  demoUrl?: string;
  content?: string;
  sections?: FeatureSection[];
  compact?: boolean;
}

const FEATURE_MODAL_CONFIG: Omit<FeatureModalConfig, "onCtaClick">[] = [
  {
    id: "provider-agnostic-mcp",
    releasedAt: "08-06-2026",
    title: "Provider-agnostic MCP servers",
    subtitle:
      "Connect an MCP server once, securely store its credentials, and reuse it across prompts and supported model providers.",
    image: "/mcp_servers_setting_page.png",
    compact: true,
    ctaLabel: "Configure MCP Servers",
    sections: [
      {
        heading: "Connect once, use everywhere",
        body: "Select saved MCP servers directly from the Tool Editor. Prompts always use the latest configuration, even when you switch providers.",
      },
      {
        heading: "Keep credentials secure",
        body: "Map authentication headers to encrypted workspace environment variables instead of storing secrets in prompts.",
      },
    ],
  },
  {
    id: "signals",
    releasedAt: "07-29-2026",
    title: "Signals",
    subtitle:
      'Ask a yes/no question about your requests, like "Did the assistant answer in the user\'s language?", and PromptLayer labels production traffic true or false, with the reasoning and the quote behind each call.',
    image: "/signals-request-log-card.png",
    ctaLabel: "Create a Signal",
    sections: [
      {
        heading: "Scope it with hooks and sampling",
        body: "Run a signal on all traffic, one prompt template and version, or everything behind a release label. Each hook gets its own sampling rate.",
      },
      {
        heading: "Test first, then filter by the results",
        body: "Try a signal on real logs before enabling it. Labels then show up on request rows, and clicking one filters your logs.",
      },
    ],
  },
  {
    id: "mock-tool-responses",
    releasedAt: "07-24-2026",
    title: "Mock tool responses",
    subtitle:
      "Turn on Mock tool responses in playground chat and every tool call your agent makes is answered by an LLM-generated mock shaped by the tool's response schema; no sandbox runs, no side effects, no waiting on real systems.",
    image: "/mock-tool-responses.png",
    ctaLabel: "Open the Playground",
    sections: [
      {
        heading: "Schema-accurate mocks you can steer",
        body: "Response schemas keep every mock correctly shaped: valid enums, realistic ids, consistent across turns. Add a plain-English scenario and the mocks play along.",
      },
      {
        heading: "Stop conditions, captured in one trace",
        body: "Listed tools pause the run so you write those responses yourself. The whole chat lands in a single trace, turn by turn.",
      },
    ],
  },
  {
    id: "sdk-evals",
    releasedAt: "07-23-2026",
    title: "Evals in the SDK",
    subtitle:
      "Define agent evals in Python or JavaScript with evaluate(...), run them locally or in CI with promptlayer eval run, and inspect every result on a PromptLayer Table.",
    mediaHook: "Create agent evals from your repo — run them in CI, score them in PromptLayer.",
    link: "https://docs.promptlayer.com/sdks/evals/overview",
    sections: [
      {
        heading: "Code-first agent evals",
        body: "Wire a dataset, your real agent runner, and scorers in one file. Evals run on your infrastructure, so they can call real tools and use your existing harness.",
      },
      {
        heading: "CI-ready CLI",
        body: "Drop *.eval.py or *.eval.ts files into your repo and run promptlayer eval run. Set a passing score and the process exits non-zero when regressions slip through.",
      },
      {
        heading: "Results land in Tables",
        body: "Each case streams to a Table sheet with traces, outputs, and scorecards, so you can debug failures in the dashboard the same way you would from a UI-built eval.",
      },
    ],
  },
  {
    id: "data-retention",
    releasedAt: "07-09-2026",
    title: "Data retention",
    subtitle:
      "Set how long request logs and traces are kept per workspace: 14, 30, or 90 days, or Forever.",
    image: "/data-retention-settings.png",
    link: "https://docs.promptlayer.com/why-promptlayer/data-retention",
    ctaLabel: "Configure Retention Policy",
    sections: [
      {
        heading: "Per-workspace policy",
        body: "Each workspace picks its own period, so you can trim high-volume workspaces and keep long-term history where you need it.",
      },
      {
        heading: "Never retroactive",
        body: "Retention is set when a record is logged. Changing the policy only affects new data — existing logs keep the retention they already had.",
      },
    ],
  },
  {
    id: "trace-filtering",
    releasedAt: "06-30-2026",
    title: "Trace filtering",
    subtitle:
      "Trace search now supports structured filters across trace-level fields, span-level fields, and same-span groups, so you can narrow production traces by status, model, tool, prompt, workflow, cost, tokens, duration, attributes, and more.",
    image: "/trace-filtering-suggestions.png",
    ctaLabel: "Ask Wrangler to Search Traces",
    sections: [
      {
        heading: "Filter across traces and spans",
        body: "Search by trace status, trace name, models used, total cost, token counts, span names, span kinds, tools, prompt and workflow references, OTel attributes, resources, and input variables.",
      },
      {
        heading: "Wrangler understands trace search",
        body: "Wrangler can now use the trace search endpoints directly, including trace filter suggestions, to find the traces behind failures, cost spikes, slow spans, or specific prompt and tool activity.",
      },
    ],
  },
  {
    id: "slack-wrangler-integration",
    releasedAt: "06-24-2026",
    title: "Wrangler in Slack",
    subtitle:
      "Bring Wrangler into your team's Slack. Connect your organization's workspace, then @mention Wrangler in any channel, or DM it directly to ask about your prompts, request logs, and analytics without leaving Slack.",
    image: "/slack-wrangler-chat.png",
    ctaLabel: "Connect Slack",
    sections: [
      {
        heading: "Mention it in channels, or DM it",
        body: "Invite Wrangler to a channel and @mention it with a question, or message it 1:1 in a DM. It replies in a thread with the same tools and insights you get in the dashboard.",
      },
      {
        heading: "Charts, right in the thread",
        body: "Ask for analytics and Wrangler renders the chart as an image and posts it straight into the conversation, no switching back to the dashboard.",
      },
      {
        heading: "Secure per-user linking",
        body: "Each teammate links their own PromptLayer account on first use, so Wrangler only answers with the data that person can access. Organization owners can connect Slack from Settings -> Slack.",
      },
    ],
  },
  {
    id: "wrangler-created-insights",
    releasedAt: "06-23-2026",
    title: "Wrangler-created insights",
    subtitle:
      "Wrangler can now turn your request logs into bespoke insights, helping you spot patterns, compare behavior, and build charts for the questions that matter to your team.",
    image: "/analytics_wrangler.GIF",
    demoUrl:
      "https://www.linkedin.com/posts/promptlayer_new-feature-alert-we-added-the-ability-activity-7473091451212550144-VygS?utm_source=share&utm_medium=member_desktop&rcm=ACoAAEAflgQBi8zf9_ZWTs5W8e3q75f1hWXD8xs",
    sections: [
      {
        heading: "Custom charts",
        body: "Ask for a chart in plain language and Wrangler can create it directly in the chat. The result stays with the conversation, so you can come back to it whenever you need it.",
        image: "/wrangler-custom-charts-chat.png",
        imageAlt: "Wrangler chat creating a custom cost chart",
        ctaLabel: "Ask Wrangler for Insights",
      },
      {
        heading: "Fullscreen mode",
        body: "Open Wrangler fullscreen when the floating panel feels too small. Long prompts, tool output, and charts all get more space.",
      },
      {
        heading: "Multiple chats in progress",
        body: "Start a separate chat for another task without losing the thread you were already in. Each conversation keeps its own messages and progress.",
      },
    ],
  },
  {
    id: "tables",
    releasedAt: "06-12-2026",
    title: "Tables",
    subtitle:
      "We replaced evals and datasets with a single, simple yet powerful primitive: Tables. Run any model over production data, score it, version it, or let your agent do it via MCP. It is an extremely versatile tool to help your team move quickly.",
    image: "/tables-model-comparison-cropped.png",
    demoUrl:
      "https://www.linkedin.com/posts/promptlayer_we-replaced-evals-and-datasets-with-a-single-activity-7470583724640903169-tn9x?utm_source=share&utm_medium=member_desktop&rcm=ACoAAEAflgQBi8zf9_ZWTs5W8e3q75f1hWXD8xs",
    sections: [
      {
        heading: "Table starter templates",
        body: "Creating a Table now opens a gallery of pre-built templates so you can skip the blank-page setup. Pick Model comparison, Brand visibility (track when AI answers mention your brand or domain), Ground truth check, or Structure check, or just tell Wrangler what you want and it'll build a runnable table for you.",
        image: "/table-presets-gallery.png",
        imageAlt: "Table starter template gallery",
      },
      {
        heading: "Aggregate scoring for Tables",
        body: "Score columns can now declare a winner (most frequent value, min, max) across a run. Comparison tables show who won; evaluation tables show passed/total so the results match the question you're actually asking.",
      },
    ],
  },
  {
    id: "promptlayer-tool-executions",
    releasedAt: "06-03-2026",
    title: "PromptLayer tool executions",
    subtitle:
      "You can now define tool executions directly in PromptLayer. Register the tools your prompts should call, configure how they execute, and keep the full tool-calling flow in one place.",
    image: "/tool-executions-function-body.png",
    demoUrl:
      "https://www.linkedin.com/posts/promptlayer_how-can-i-test-my-llm-application-if-i-rely-activity-7465046682775412737-FJc5?utm_source=share&utm_medium=member_desktop&rcm=ACoAAEAflgQBi8zf9_ZWTs5W8e3q75f1hWXD8xs",
  },
];

const latestFeatureReleaseDate = FEATURE_MODAL_CONFIG.reduce(
  (latest, feature) =>
    moment(feature.releasedAt, FEATURE_DATE_FORMAT).isAfter(moment(latest, FEATURE_DATE_FORMAT))
      ? feature.releasedAt
      : latest,
  FEATURE_MODAL_CONFIG[0].releasedAt,
);
const featureVersion = `changelog-${latestFeatureReleaseDate}`;

const isUserSignedUpAfterRelease = (createdAt?: string) => {
  if (!createdAt) {
    return false;
  }

  const featureReleaseDate = moment(latestFeatureReleaseDate, FEATURE_DATE_FORMAT);
  return moment(createdAt).isAfter(featureReleaseDate);
};

const isEntityPreviewScreenshotMode = () =>
  typeof document !== "undefined" &&
  document.documentElement.dataset.entityPreviewScreenshot === "true";

const isPreviewMode = () =>
  typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY_PREVIEW_MODE) === "true";

export const openNewFeatureModal = () => {
  window.dispatchEvent(new Event(OPEN_NEW_FEATURE_MODAL_EVENT));
};

export const isNewFeatureModalUnseen = (createdAt?: string) => {
  if (isPreviewMode()) {
    return false;
  }

  if (isUserSignedUpAfterRelease(createdAt)) {
    return false;
  }

  const lastSeenFeatureVersion = localStorage.getItem(STORAGE_KEY_VERSION);
  const isDismissed = localStorage.getItem(`${STORAGE_KEY_DISMISSED}_${featureVersion}`);

  return lastSeenFeatureVersion !== featureVersion && isDismissed !== "true";
};

export default function NewFeatureModal() {
  const location = useLocation();
  const navigate = useNavigate();
  const isLocalStoragePreviewMode = isPreviewMode();
  const isPreviewRenderer =
    isLocalStoragePreviewMode ||
    location.pathname.includes("/entity-preview-render/") ||
    isEntityPreviewScreenshotMode();
  const { user, activeWorkspaceId } = useUser();
  const { created_at } = user;

  const isSignedUpAfterRelease = isUserSignedUpAfterRelease(created_at);

  const [lastSeenFeatureVersion, setLastSeenFeatureVersion] = useLocalStorage(
    STORAGE_KEY_VERSION,
    "",
  );

  const [isDismissed, setIsDismissed] = useLocalStorage(
    `${STORAGE_KEY_DISMISSED}_${featureVersion}`,
    "false",
  );

  const [isOpen, setIsOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(0);
  const [bodyHeight, setBodyHeight] = useState<number | undefined>();
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const bodyContentRef = useRef<HTMLDivElement>(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (isPreviewRenderer) {
      return;
    }

    if (isSignedUpAfterRelease && lastSeenFeatureVersion !== featureVersion) {
      setLastSeenFeatureVersion(featureVersion);
      setIsDismissed("true");
    }
  }, [
    isPreviewRenderer,
    isSignedUpAfterRelease,
    lastSeenFeatureVersion,
    setLastSeenFeatureVersion,
    setIsDismissed,
  ]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setLastSeenFeatureVersion(featureVersion);
    setIsDismissed("true");
    window.dispatchEvent(new Event(NEW_FEATURE_MODAL_SEEN_EVENT));
  }, [setLastSeenFeatureVersion, setIsDismissed]);

  const openWranglerCustomChartsPrompt = useCallback(() => {
    const prompt =
      "Analyze my workspace data and request analytics. Probe what data is available, identify useful patterns or anomalies, and create custom charts that would give me actionable insights.";

    closeModal();
    window.dispatchEvent(
      new CustomEvent<OpenVibeChatEventDetail>(OPEN_VIBECHAT_EVENT, {
        detail: { prompt, newChat: true },
      }),
    );
  }, [closeModal]);

  const openWranglerTraceSearchPrompt = useCallback(() => {
    const prompt =
      "Search my traces for recent failures, slow spans, high-cost traces, and notable prompt, workflow, or tool activity. Use trace filters and summarize the most important traces to inspect.";

    closeModal();
    window.dispatchEvent(
      new CustomEvent<OpenVibeChatEventDetail>(OPEN_VIBECHAT_EVENT, {
        detail: { prompt, newChat: true },
      }),
    );
  }, [closeModal]);

  const getSectionCtaClick = useCallback(
    (featureId: string, sectionHeading: string) => {
      if (featureId === "wrangler-created-insights" && sectionHeading === "Custom charts") {
        return openWranglerCustomChartsPrompt;
      }

      return undefined;
    },
    [openWranglerCustomChartsPrompt],
  );

  const connectSlack = useCallback(() => {
    closeModal();
    if (activeWorkspaceId) {
      navigate(`/workspace/${activeWorkspaceId}/settings/slack`);
    }
  }, [closeModal, navigate, activeWorkspaceId]);

  const openRetentionSettings = useCallback(() => {
    closeModal();
    if (activeWorkspaceId) {
      navigate(`/workspace/${activeWorkspaceId}/settings/retention-policy`);
    }
  }, [closeModal, navigate, activeWorkspaceId]);

  const openPlayground = useCallback(() => {
    closeModal();
    if (activeWorkspaceId) {
      navigate(`/workspace/${activeWorkspaceId}/playground?mode=chat`);
    }
  }, [closeModal, navigate, activeWorkspaceId]);

  const openNewSignal = useCallback(() => {
    closeModal();
    if (activeWorkspaceId) {
      navigate(`/workspace/${activeWorkspaceId}/signals/new`);
    }
  }, [closeModal, navigate, activeWorkspaceId]);

  const openMcpServersSettings = useCallback(() => {
    closeModal();
    if (activeWorkspaceId) {
      navigate(`/workspace/${activeWorkspaceId}/settings/mcp-servers`);
    }
  }, [closeModal, navigate, activeWorkspaceId]);

  const getFeatureCtaClick = useCallback(
    (featureId: string) => {
      if (featureId === "provider-agnostic-mcp") {
        return openMcpServersSettings;
      }
      if (featureId === "signals") {
        return openNewSignal;
      }
      if (featureId === "mock-tool-responses") {
        return openPlayground;
      }
      if (featureId === "slack-wrangler-integration") {
        return connectSlack;
      }
      if (featureId === "trace-filtering") {
        return openWranglerTraceSearchPrompt;
      }
      if (featureId === "data-retention") {
        return openRetentionSettings;
      }

      return undefined;
    },
    [
      openNewSignal,
      connectSlack,
      openWranglerTraceSearchPrompt,
      openRetentionSettings,
      openPlayground,
      openMcpServersSettings,
    ],
  );

  const modals: FeatureModalConfig[] = FEATURE_MODAL_CONFIG.map(feature => ({
    ...feature,
    onCtaClick: getFeatureCtaClick(feature.id),
    sections: feature.sections?.map(section => ({
      ...section,
      onCtaClick: getSectionCtaClick(feature.id, section.heading),
    })),
  }));

  const updateBodyHeight = useCallback(() => {
    if (!bodyContentRef.current) {
      return;
    }

    const maxBodyHeight = window.innerHeight * 0.92 - 76 - 73;
    setBodyHeight(Math.min(bodyContentRef.current.scrollHeight, maxBodyHeight));
  }, []);

  useLayoutEffect(() => {
    updateBodyHeight();
    bodyScrollRef.current?.scrollTo({ top: 0 });
  }, [activeModal, updateBodyHeight]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    updateBodyHeight();
    window.addEventListener("resize", updateBodyHeight);

    return () => {
      window.removeEventListener("resize", updateBodyHeight);
    };
  }, [isOpen, updateBodyHeight]);

  useEffect(() => {
    const handleOpenNewFeatureModal = () => {
      if (isPreviewRenderer) {
        return;
      }

      setActiveModal(0);
      setIsOpen(true);
    };

    window.addEventListener(OPEN_NEW_FEATURE_MODAL_EVENT, handleOpenNewFeatureModal);

    return () => {
      window.removeEventListener(OPEN_NEW_FEATURE_MODAL_EVENT, handleOpenNewFeatureModal);
    };
  }, [isPreviewRenderer]);

  useEffect(() => {
    if (isPreviewRenderer) {
      return;
    }

    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    const shouldShowModal =
      !isSignedUpAfterRelease &&
      lastSeenFeatureVersion !== featureVersion &&
      isDismissed !== "true";

    if (shouldShowModal) {
      setIsOpen(true);
    }
  }, [isPreviewRenderer, lastSeenFeatureVersion, isSignedUpAfterRelease, isDismissed]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        closeModal();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, closeModal]);

  const nextModal = () => {
    setActiveModal(prev => (prev + 1) % modals.length);
  };

  const prevModal = () => {
    setActiveModal(prev => (prev === 0 ? modals.length - 1 : prev - 1));
  };

  const goToModal = (index: number) => {
    if (index === activeModal) {
      return;
    }

    setActiveModal(index);
  };

  if (isPreviewRenderer) {
    return null;
  }

  const renderCarouselControls = () => {
    if (modals.length <= 1) {
      return null;
    }

    return (
      <div className="flex items-center justify-between border-t border-gray-100 px-6 py-5">
        <div className="flex items-center gap-1.5">
          {modals.map((modal, index) => (
            <button
              key={modal.title}
              type="button"
              className={cn(
                "h-2 rounded-full bg-gray-300 transition-all hover:bg-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
                index === activeModal ? "w-5 bg-blue-600" : "w-2",
              )}
              onClick={() => goToModal(index)}
              aria-label={`Show feature ${index + 1}`}
              aria-current={index === activeModal ? "true" : undefined}
            />
          ))}
        </div>
        <div className="flex items-center gap-5 text-gray-400">
          <button
            type="button"
            className="rounded-full p-1 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            onClick={prevModal}
            aria-label="Previous feature"
          >
            <ChevronLeft className="size-5" />
          </button>
          <button
            type="button"
            className="rounded-full p-1 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            onClick={nextModal}
            aria-label="Next feature"
          >
            <ChevronRight className="size-5" />
          </button>
        </div>
      </div>
    );
  };

  const activeFeature = modals[activeModal];
  const formattedReleaseDate = moment(activeFeature.releasedAt, FEATURE_DATE_FORMAT).format(
    "MMMM D, YYYY",
  );

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && closeModal()}>
      <DialogContent
        className="z-[9999] max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-[576px] gap-0 overflow-hidden rounded-xl border border-gray-200 bg-white p-0 shadow-2xl sm:rounded-xl"
        overlayClassName="z-[9998] bg-gray-950/35 backdrop-blur-sm"
      >
        <div className="flex items-start justify-between border-b border-gray-100 px-6 py-3.5">
          <div className="flex items-start gap-2.5">
            <div className="flex size-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <Sparkles className="size-4" />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold leading-5 text-gray-950">
                What&apos;s new
              </DialogTitle>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <DialogDescription className="text-xs text-gray-500">
                  Released {formattedReleaseDate}
                </DialogDescription>
                {activeFeature.demoUrl && (
                  <a
                    href={activeFeature.demoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                  >
                    Watch the demo
                    <ExternalLink className="size-3" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex max-h-[calc(92vh-76px)] flex-col">
          <div
            ref={bodyScrollRef}
            className="overflow-y-auto transition-[height] duration-300 ease-out"
            style={{ height: bodyHeight }}
          >
            <div key={activeModal} ref={bodyContentRef}>
              <article className={cn("px-6 py-4", activeFeature.compact && "py-3")}>
                <h2 className="text-xl font-semibold tracking-tight text-gray-950">
                  {activeFeature.title}
                </h2>
              </article>

              <div
                className={cn(
                  "relative flex items-center justify-center overflow-hidden bg-gray-50 px-6 py-3",
                  activeFeature.compact && "px-4 py-2",
                )}
              >
                {activeFeature.image && (
                  <img
                    className="absolute inset-0 h-full w-full scale-110 object-cover opacity-20 blur-md"
                    src={activeFeature.image}
                    alt=""
                    aria-hidden="true"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/75 to-white/95" />
                {activeFeature.video && (
                  <iframe
                    className="relative z-10 h-full w-full rounded-lg border border-gray-200 bg-white shadow-lg"
                    src={activeFeature.video}
                    title={activeFeature.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    onLoad={updateBodyHeight}
                  />
                )}
                {activeFeature.image && (
                  <img
                    className={cn(
                      "relative z-10 h-[21rem] w-auto max-w-full rounded-lg border border-gray-200 bg-white object-contain shadow-sm",
                      activeFeature.compact && "h-[14rem]",
                    )}
                    src={activeFeature.image}
                    alt={activeFeature.title}
                    onLoad={updateBodyHeight}
                  />
                )}
                {!activeFeature.image && !activeFeature.video && (
                  <div className="relative z-10 flex w-full flex-col items-center rounded-xl border border-blue-100 bg-white/90 px-8 py-10 text-center">
                    <div className="flex size-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                      <Sparkles className="size-6" />
                    </div>
                    <p className="mt-4 text-sm font-semibold text-gray-950">
                      {activeFeature.title}
                    </p>
                    <p className="mt-2 text-sm leading-6 text-gray-600">
                      {activeFeature.mediaHook ?? "See what's new below."}
                    </p>
                  </div>
                )}
              </div>

              <article className={cn("px-6 py-5", activeFeature.compact && "py-4")}>
                <p className="text-sm leading-6 text-gray-600">{activeFeature.subtitle}</p>
                {activeFeature.sections && (
                  <div
                    className={cn(
                      "mt-7 space-y-6",
                      activeFeature.compact && "mt-4 grid grid-cols-2 gap-4 space-y-0",
                    )}
                  >
                    {activeFeature.sections.map(section => (
                      <section key={section.heading}>
                        <h3 className="text-sm font-semibold text-gray-950">{section.heading}</h3>
                        <p
                          className={cn(
                            "mt-2 text-sm leading-6 text-gray-600",
                            activeFeature.compact && "mt-1 text-xs leading-5",
                          )}
                        >
                          {section.body}
                        </p>
                        {section.image && (
                          <img
                            className="mt-4 w-full rounded-lg border border-gray-200 bg-white object-contain shadow-sm"
                            src={section.image}
                            alt={section.imageAlt ?? section.heading}
                            onLoad={updateBodyHeight}
                          />
                        )}
                        {section.ctaLabel && (
                          <Button
                            type="button"
                            variant="secondaryLightOutline"
                            className="mt-4 h-9 gap-2 rounded-md border-blue-300 px-4 text-sm font-medium"
                            onClick={section.onCtaClick}
                          >
                            {section.ctaLabel}
                            <ArrowRight className="size-4" />
                          </Button>
                        )}
                      </section>
                    ))}
                  </div>
                )}
                {activeFeature.content && (
                  <p className="mt-4 text-sm leading-6 text-gray-600">{activeFeature.content}</p>
                )}
                <div
                  className={cn(
                    "mt-6 flex flex-wrap items-center gap-2",
                    activeFeature.compact && "mt-4",
                  )}
                >
                  {activeFeature.ctaLabel && (
                    <Button
                      type="button"
                      variant="secondaryLightOutline"
                      className="h-9 gap-2 rounded-md border-blue-300 px-4 text-sm font-medium"
                      onClick={activeFeature.onCtaClick}
                    >
                      {activeFeature.ctaLabel}
                      <ArrowRight className="size-4" />
                    </Button>
                  )}
                  {activeFeature.link && (
                    <Button
                      asChild
                      variant="ghostGray"
                      className="h-9 gap-2 rounded-md px-4 text-sm font-medium text-gray-700"
                    >
                      <a href={activeFeature.link} target="_blank" rel="noreferrer">
                        <BookOpen className="size-4" />
                        Read the docs
                      </a>
                    </Button>
                  )}
                </div>
              </article>
            </div>
          </div>
          {renderCarouselControls()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
